import { isDefiniteShopRejection } from "./shopMutationOutcome";
import React, { useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { firebaseAuth } from "../../../components/firebase/auth";
import { ShopApiError, shopAdminApi, type ShopCaseDetail, type ShopCaseKind } from "./shopAdminApi";
import { caseQueueUrlQuery, caseQueueStates, caseQueueIssueLabels, caseAssignmentLabels, caseTopicLabels, type CaseQueuePage } from "./caseQueueModel";
import "./webshopCatalog.css";
import CaseReviewHistory from "./CaseReviewHistory";
import CaseEmailDelivery from "./CaseEmailDelivery";
import CaseOrderCheck from "./CaseOrderCheck";
import { validCaseAssignment, type CaseAssignmentAction } from "./caseAssignmentModel";
import CaseTransferRecipient from "./CaseTransferRecipient";
import type { CaseRecipient } from "./caseTransferModel";
import type { CaseOperationBody } from "./caseOperationReceiptModel";

type Decision = "eligible" | "ineligible" | "needs_information";
type ReviewItem = { index: number; decision: Decision; reason: string };
type ReviewIntent = { uncertain?: boolean; recipientLabel?: string; key: string; id: string; kind: ShopCaseKind; status: "saving" | "unknown" | "blocked";
  checkingReceipt?: boolean; receipt?: { state: "confirmed"; recordedAt: string } | { state: "unconfirmed" | "unavailable" };
  body: CaseOperationBody };
const stateLabel: Record<string, string> = {
  received: "Mottagen", triaged: "Under handläggning", resolved: "Löst",
  under_review: "Under granskning", reviewed: "Granskad",
};
const dateLabel = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString("sv-SE", { timeZone: "Europe/Stockholm" });
};

export default function WebshopCaseQueue({ kind, canManage = false, canReadOrders = false, previewOnly = false }:
  { kind: ShopCaseKind; canManage?: boolean; canReadOrders?: boolean; previewOnly?: boolean }) {
  const [user] = useAuthState(firebaseAuth);
  const location = useLocation(), navigate = useNavigate();
  const path = kind === "support" ? "support" : "withdrawals";
  const detailId = location.pathname.startsWith(`/webshop/${path}/`) ? location.pathname.slice(`/webshop/${path}/`.length) : null;
  const [page, setPage] = useState<CaseQueuePage | null>(null);
  const [storedDetail, setDetail] = useState<ShopCaseDetail | null>(null);
  const [reviewItems, setReviewItems] = useState<ReviewItem[]>([]);
  const [reason, setReason] = useState("");
  const [recipient, setRecipient] = useState<CaseRecipient | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deniedFor, setDeniedFor] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [loadedFor, setLoadedFor] = useState("");
  const queueQuery = caseQueueUrlQuery(kind, location.search);
  const filters = queueQuery?.filters, cursor = queueQuery?.cursor;
  const detailKey = `${user?.uid}:${kind}:${detailId}:${canManage}:${previewOnly}:${refresh}`;
  const loadKey = detailId ? detailKey : `${detailKey}:${filters?.state}:${filters?.assignment}:${cursor}:${queueQuery !== null}`;
  const detail = loadedFor === detailKey ? storedDetail : null;
  const visiblePage = loadedFor === loadKey ? page : null;
  const scope = `${user?.uid}:${kind}:${detailId}`;
  const intents = useRef(new Map<string, ReviewIntent>());
  const [, renderIntent] = useState(0);
  const intent = intents.current.get(scope), saving = intent?.status === "saving", frozen = !!intent;
  const visibleContext = useRef("");
  visibleContext.current = `${scope}:${canManage}:${previewOnly}`;
  const accessDenied = deniedFor === visibleContext.current;
  const receiptCheckButton = useRef<HTMLButtonElement>(null), receiptConfirmedButton = useRef<HTMLButtonElement>(null);
  const focusReceiptAfterRender = useRef(false);
  const viewFence = useRef({ context: "", generation: 0 });
  if (viewFence.current.context !== visibleContext.current) {
    viewFence.current = { context: visibleContext.current, generation: viewFence.current.generation + 1 };
    focusReceiptAfterRender.current = false;
  }
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (focusReceiptAfterRender.current && receiptConfirmedButton.current) {
      focusReceiptAfterRender.current = false;
      if (document.activeElement === document.body) receiptConfirmedButton.current.focus();
    }
  });

  useEffect(() => {
    if (!user || !canManage || previewOnly || (!detailId && !queueQuery)) {
      setLoading(false); setError(""); setDetail(null); setPage(null); setLoadedFor(""); return;
    }
    let live = true;
    const readContext = visibleContext.current;
    setLoading(true); setError(""); setDetail(null); setPage(null); setLoadedFor(""); setDeniedFor("");
    const task = detailId ? shopAdminApi.caseDetail(user, kind, detailId) : shopAdminApi.cases(user, kind, filters!, cursor);
    task.then((value) => {
      if (!live) return;
      setLoading(false);
      if ("cases" in value) { setPage(value); setLoadedFor(loadKey); }
      else {
        setDetail(value); setLoadedFor(detailKey); setReason(""); setRecipient(null);
        setReviewItems((value.goods || []).map((_, index) => value.decisions?.find((item) => item.index === index) ||
          { index, decision: "needs_information", reason: "" }));
      }
    }).catch((caught: unknown) => {
      if (live) {
        setLoading(false);
        if (caught instanceof ShopApiError && [401, 403].includes(caught.status)) {
          viewFence.current.generation += 1; setDeniedFor(readContext);
        }
        setError(caught instanceof ShopApiError && caught.status === 404 ? "Ärendet finns inte." : "Ärendet kunde inte hämtas. Försök igen.");
      }
    });
    return () => { live = false; };
  // Primitive filter keys avoid refetching details when history-only URL parameters change.
  }, [user, kind, detailId, canManage, previewOnly, refresh, detailKey, loadKey, filters?.state, filters?.assignment, cursor]); // eslint-disable-line react-hooks/exhaustive-deps

  const setQueueQuery = (changes: { state?: string; assignment?: string; cursor?: string }) => {
    const search = new URLSearchParams(location.search);
    if (!queueQuery) { search.set("caseState", "all"); search.set("caseAssignment", "all"); }
    if (changes.state !== undefined) search.set("caseState", changes.state);
    if (changes.assignment !== undefined) search.set("caseAssignment", changes.assignment);
    search.delete("caseCursor"); search.delete("caseHistoryCursor"); search.delete("caseAssignmentHistoryCursor");
    if (changes.cursor) search.set("caseCursor", changes.cursor);
    navigate(`${location.pathname}${search.toString() ? `?${search}` : ""}`);
  };
  const refreshList = () => { setQueueQuery({}); setRefresh((value) => value + 1); };
  const listSearch = new URLSearchParams(location.search); listSearch.delete("caseHistoryCursor"); listSearch.delete("caseAssignmentHistoryCursor");
  const listUrl = `/webshop/${path}${listSearch.toString() ? `?${listSearch}` : ""}`;

  const submit = async (assignment?: CaseAssignmentAction | "transfer") => {
    if (!user || !canManage || previewOnly || accessDenied || !detail || loading) return;
    const existing = intents.current.get(scope);
    if (existing && (existing.status !== "unknown" || existing.checkingReceipt || existing.receipt?.state === "confirmed")) return;
    if (!existing && (assignment === "transfer" ? !canTransfer : assignment ? !canAssign || assignment !== availableAssignment : !canSubmit)) return;
    const pending: ReviewIntent = existing || { id: detail.id, kind, key: crypto.randomUUID(), status: "saving", recipientLabel: assignment === "transfer" ? recipient?.label : undefined,
      body: assignment === "transfer" ? { expectedRevision: detail.revision, recipientUid: recipient!.uid, reason: reason.trim() } : assignment ?
      { expectedRevision: detail.revision, assignment, reason: reason.trim() } : kind === "support"
      ? { expectedRevision: detail.revision, state: detail.state === "received" ? "triaged" : "resolved", reason: reason.trim() }
      : { expectedRevision: detail.revision, items: reviewItems.map((item) => ({ ...item, reason: item.reason.trim() })), reason: reason.trim() } };
    pending.status = "saving"; pending.receipt = undefined; intents.current.set(scope, pending);
    const context = visibleContext.current;
    const generation = viewFence.current.generation;
    const current = () => mounted.current && visibleContext.current === context && viewFence.current.generation === generation;
    renderIntent((value) => value + 1); setError("");
    try {
      if ("recipientUid" in pending.body) await shopAdminApi.transferCase(user, pending.kind, pending.id, pending.body, pending.key);
      else if ("assignment" in pending.body) await shopAdminApi.assignCase(user, pending.kind, pending.id, pending.body, pending.key);
      else if (pending.kind === "support" && "state" in pending.body) await shopAdminApi.triageSupport(user, pending.id, pending.body, pending.key);
      else if ("items" in pending.body) await shopAdminApi.reviewWithdrawal(user, pending.id, pending.body, pending.key);
      else throw new Error("Invalid review intent");
      intents.current.delete(scope);
      if (!current()) return;
      const search = new URLSearchParams(location.search); search.delete("caseHistoryCursor"); search.delete("caseAssignmentHistoryCursor");
      navigate(`${location.pathname}${search.toString() ? `?${search}` : ""}`, { replace: true });
      setReason(""); renderIntent((value) => value + 1);
      setRefresh((value) => value + 1);
    } catch (caught) {
      pending.status = isDefiniteShopRejection(caught, pending.uncertain) ? "blocked" : "unknown";
      if (pending.status === "unknown") pending.uncertain = true;
      if (current()) {
        if (caught instanceof ShopApiError && [401, 403].includes(caught.status)) {
          viewFence.current.generation += 1;
          setDeniedFor(context); setDetail(null); setPage(null); setLoadedFor(""); setReason(""); setReviewItems([]);
        }
        renderIntent((value) => value + 1);
      }
    }
  };
  const checkOriginalReceipt = async () => {
    const pending = intents.current.get(scope);
    if (!user || !canManage || previewOnly || accessDenied || !pending || pending.status !== "unknown" ||
        pending.checkingReceipt || pending.receipt?.state === "confirmed") return;
    const context = visibleContext.current, generation = viewFence.current.generation;
    const startedWithFocus = document.activeElement === receiptCheckButton.current;
    const current = () => mounted.current && visibleContext.current === context && viewFence.current.generation === generation && intents.current.get(scope) === pending;
    pending.checkingReceipt = true; pending.receipt = undefined; renderIntent((value) => value + 1);
    try {
      const receipt = await shopAdminApi.caseOperationReceipt(user, pending.kind, pending.id, pending.body, pending.key);
      if (!current()) return;
      pending.receipt = receipt.outcome === "confirmed" ? { state: "confirmed", recordedAt: receipt.recordedAt } : { state: "unconfirmed" };
      if (receipt.outcome === "confirmed") focusReceiptAfterRender.current = document.activeElement === receiptCheckButton.current ||
        (startedWithFocus && document.activeElement === document.body);
    } catch (caught) {
      if (!current()) return;
      pending.receipt = { state: "unavailable" };
      if (caught instanceof ShopApiError && [401, 403].includes(caught.status)) hideDenied();
    } finally {
      pending.checkingReceipt = false;
      if (mounted.current) renderIntent((value) => value + 1);
    }
  };
  const showCurrentAfterReceipt = () => {
    const pending = intents.current.get(scope);
    if (!user || !canManage || previewOnly || accessDenied || pending?.receipt?.state !== "confirmed" || pending.checkingReceipt) return;
    intents.current.delete(scope);
    const search = new URLSearchParams(location.search); search.delete("caseHistoryCursor"); search.delete("caseAssignmentHistoryCursor");
    navigate(`${location.pathname}${search.toString() ? `?${search}` : ""}`, { replace: true });
    setReason(""); renderIntent((value) => value + 1); setRefresh((value) => value + 1);
  };
  const reloadReview = () => { if (intents.current.get(scope)?.status === "blocked") intents.current.delete(scope); renderIntent((value) => value + 1); setReason(""); setRefresh((value) => value + 1); };
  const title = kind === "support" ? "Kundärenden" : "Ångrade köp";
  const owner = detail && validCaseAssignment(detail.assignment) ? detail.assignment : "unknown";
  const open = !!detail && (kind === "support" ? ["received", "triaged"] : ["received", "under_review"]).includes(detail.state);
  const availableAssignment: CaseAssignmentAction | null = owner === "current_staff" ? "unassigned" : owner === "unassigned" && open ? "mine" : null;
  const validRevision = !!detail && Number.isSafeInteger(detail.revision) && detail.revision >= 0 && detail.revision < Number.MAX_SAFE_INTEGER;
  const canAssign = !!availableAssignment && validRevision && !!reason.trim() && !frozen && !loading;
  const canTransfer = owner === "current_staff" && open && validRevision && !!recipient && recipient.uid !== user?.uid && !!reason.trim() && !frozen && !loading;
  const chosenRecipient = intent && "recipientUid" in intent.body ? { uid: intent.body.recipientUid, label: intent.recipientLabel || "Vald mottagare" } : recipient;
  const hideDenied = () => { viewFence.current.generation += 1; setDeniedFor(visibleContext.current); setDetail(null); setPage(null); setLoadedFor(""); setReason(""); setReviewItems([]); setRecipient(null); };
  const canSubmit = !!detail && Number.isSafeInteger(detail.revision) && detail.revision >= 0 && detail.revision < Number.MAX_SAFE_INTEGER &&
    !!reason.trim() && !frozen && !loading && (kind === "support"
    ? ["received", "triaged"].includes(detail.state)
    : ["received", "under_review"].includes(detail.state) && reviewItems.length === detail.goods?.length &&
      reviewItems.every((item) => !!item.reason.trim()));

  return <div className="DashBoardTabInner FreshDashboardTab WebshopCatalog">
    <div className="FreshTabToolbar"><div><p className="FreshEyebrow">Webshop</p><h2 className="FreshTabTitle">{title}</h2>
      <p className="FreshTabSummary">Privata ärenden från webbshopens formulär. Granskning ändrar inte betalning eller lager.</p></div>
      {detailId ? <><Button component={Link} to={listUrl}>Till ärendelistan</Button>
        {!previewOnly && canManage && <Button onClick={() => accessDenied ? reloadReview() : setRefresh((value) => value + 1)}
          disabled={loading || (frozen && !accessDenied)}>Uppdatera ärende</Button>}</>
        : !previewOnly && canManage && <Button onClick={refreshList} disabled={loading}>Uppdatera</Button>}</div>
    {previewOnly ? <Alert severity="info">Ärendekön blir tillgänglig när shopAdminApi är driftsatt. Inga kunduppgifter läses i förhandsvisningen.</Alert>
      : !canManage || !user || accessDenied ? <Alert severity="warning">Du saknar behörighet att läsa kundärenden.</Alert> : <>
        {error && <Alert severity="error">{error}</Alert>}
        {loading && <p role="status"><CircularProgress size={20} /> Hämtar ärenden…</p>}
        {!detailId && <>
          {!queueQuery ? <Alert severity="warning">Listans filter eller sidreferens är ogiltig. Uppdatera till första sidan.</Alert> : <div className="FreshTabControls">
            <TextField select label="Ärendestatus" size="small" value={filters!.state} disabled={loading}
              onChange={(event) => setQueueQuery({ state: event.target.value })}>
              <MenuItem value="all">Alla statusar</MenuItem>{caseQueueStates[kind].map((state) => <MenuItem key={state} value={state}>{stateLabel[state]}</MenuItem>)}
            </TextField>
            <TextField select label="Handläggare" size="small" value={filters!.assignment} disabled={loading}
              onChange={(event) => setQueueQuery({ assignment: event.target.value })}>
              <MenuItem value="all">Alla ärenden</MenuItem><MenuItem value="mine">Mina ärenden</MenuItem>
            </TextField>
          </div>}
          <p>Högst 25 ärenden per sida, ordnade efter ärendereferens. Mottagningsdatum visas för varje ärende. Uppdatera för att hämta ändringar.</p>
          {visiblePage && !loading && <div className="WebshopCatalogList">
          <p>Hämtat {dateLabel(visiblePage.asOf)}. Sidan visar inte hela kön.</p>
          {visiblePage.cases.length === 0 && !error && <p>Inga ärenden matchar filtren på denna sida.</p>}
          {visiblePage.cases.map((item) => <article key={item.id} className="WebshopCatalogProduct">
            <h3>{kind === "support" ? caseTopicLabels[item.topic || "unknown"] : "Begäran om ångrat köp"}</h3>
            <p>{item.receivedAt ? dateLabel(item.receivedAt) : "Mottagningsdatum saknas"} · {stateLabel[item.state] || "Okänd status"}</p>
            <p>{caseAssignmentLabels[item.assignment]}</p>
            {item.integrityIssues.length > 0 && <Alert severity="warning">{item.integrityIssues.map((issue) => caseQueueIssueLabels[issue]).join(". ")}.</Alert>}
            <Button component={Link} to={`/webshop/${path}/${item.id}${listSearch.toString() ? `?${listSearch}` : ""}`}>Visa ärende</Button>
          </article>)}
          {cursor && <Button onClick={refreshList}>Till första sidan</Button>}
          {visiblePage.nextCursor && <Button onClick={() => setQueueQuery({ cursor: visiblePage.nextCursor! })}>Nästa sida</Button>}
        </div>}</>}
        {detail && !loading && <article className="WebshopCatalogProduct WebshopCaseDetail">
          <h3>{kind === "support" ? detail.topic || "Kundärende" : "Begäran om ångrat köp"}</h3>
          <p>Mottaget {dateLabel(detail.receivedAt)} · {stateLabel[detail.state] || detail.state}</p>
          <p>{caseAssignmentLabels[owner]}</p>
          <p><strong>Kontakt:</strong> {detail.name} · <a href={`mailto:${detail.email}`}>{detail.email}</a></p>
          {detail.orderReference && <p><strong>Angivet ordernummer:</strong> {detail.orderReference} (inte verifierat)</p>}
          {detail.identifyingDetails && <p><strong>Identifierande uppgifter:</strong> {detail.identifyingDetails}</p>}
          {detail.message && <div><strong>Meddelande:</strong><p className="WebshopCaseMessage">{detail.message}</p></div>}
          {detail.goods && <div><strong>Varor i begäran:</strong><ul>{detail.goods.map((good, index) =>
            <li key={index}>{good.quantity} × {good.description}</li>)}</ul></div>}
          <CaseEmailDelivery delivery={detail.emailDelivery} />
          <CaseOrderCheck user={user} kind={kind} caseId={detail.id} revision={detail.revision} initialReference={detail.orderReference}
            canRead={canManage && canReadOrders} previewOnly={previewOnly} blocked={frozen} onDenied={hideDenied}
            onRefreshCase={() => setRefresh((value) => value + 1)} />
          <CaseReviewHistory user={user} kind={kind} caseId={detail.id} canRead={canManage} previewOnly={previewOnly} refreshVersion={refresh} onDenied={hideDenied} />
          <CaseReviewHistory user={user} kind={kind} caseId={detail.id} canRead={canManage} previewOnly={previewOnly} refreshVersion={refresh} assignmentOnly onDenied={hideDenied} />
          {intent?.status === "unknown" && <section aria-label="Kontroll av ursprunglig åtgärd">
            {intent.receipt?.state === "confirmed" ? <Alert severity="success">
              Den ursprungliga åtgärden är verifierad som registrerad {dateLabel(intent.receipt.recordedAt)}. Detta bekräftar ingen betalning, återbetalning eller levererad e-post.
              <Button ref={receiptConfirmedButton} onClick={showCurrentAfterReceipt}>Visa aktuell version</Button>
            </Alert> : <Alert severity="warning">Resultatet av åtgärden är okänt. Kontrollera registreringen eller upprepa exakt samma åtgärd innan du ändrar uppgifterna.
              Vid omladdning av hela sidan försvinner den sparade begäran; kontrollera ärendet och historiken innan en ny granskning.
              <Button ref={receiptCheckButton} onClick={() => void checkOriginalReceipt()} disabled={intent.checkingReceipt}>Kontrollera registrering</Button>
              {intent.checkingReceipt && <p role="status">Kontrollerar den ursprungliga registreringen…</p>}
              {intent.receipt?.state === "unconfirmed" && <p>Registreringen kunde inte bekräftas. Det betyder inte att åtgärden misslyckades. Den ursprungliga begäran behålls.</p>}
              {intent.receipt?.state === "unavailable" && <p>Kontrollen kunde inte genomföras. Resultatet är fortfarande okänt och den ursprungliga begäran behålls.</p>}
            </Alert>}
          </section>}
          {intent?.status === "blocked" && <Alert severity="warning">Åtgärden avvisades eller ärendet har ändrats. Hämta aktuellt ärende och granska igen.
            <Button onClick={reloadReview}>Hämta aktuell version</Button></Alert>}
          {kind === "withdrawal" && (["received", "under_review"].includes(detail.state) || frozen) &&
            (intent && "items" in intent.body ? intent.body.items : reviewItems).map((item, index) =>
            <div className="WebshopCaseReviewItem" key={item.index}>
              <strong>{detail.goods?.[index]?.description || `Vara ${index + 1}`}</strong>
              <TextField select label="Bedömning" value={item.decision} size="small" disabled={frozen}
                onChange={(event) => setReviewItems((current) => current.map((row) => row.index === item.index ?
                  { ...row, decision: event.target.value as Decision } : row))}>
                <MenuItem value="needs_information">Behöver mer information</MenuItem>
                <MenuItem value="eligible">Berättigad efter granskning</MenuItem>
                <MenuItem value="ineligible">Inte berättigad efter granskning</MenuItem>
              </TextField>
              <TextField label="Skäl för varan" value={item.reason} size="small" fullWidth multiline maxRows={4} disabled={frozen}
                onChange={(event) => setReviewItems((current) => current.map((row) => row.index === item.index ?
                  { ...row, reason: event.target.value } : row))} inputProps={{ maxLength: 500 }} />
            </div>)}
          {((kind === "support" && ["received", "triaged"].includes(detail.state)) ||
            (kind === "withdrawal" && ["received", "under_review"].includes(detail.state)) || availableAssignment || frozen) && <>
            <TextField label="Skäl / handläggningsnotering" value={intent?.body.reason ?? reason} fullWidth multiline minRows={2} disabled={frozen}
              onChange={(event) => setReason(event.target.value)} inputProps={{ maxLength: 500 }} />
            {(open || frozen) && intent?.receipt?.state !== "confirmed" && <Button variant="contained" onClick={() => void submit()}
              disabled={saving || loading || intent?.checkingReceipt || !(intent?.status === "unknown" || canSubmit)}>
              {saving ? "Sparar åtgärd…" : intent?.status === "unknown" ? "Upprepa samma åtgärd" :
                kind === "support" ? detail.state === "received" ? "Ta ärendet" : "Markera löst" : "Spara bedömning"}</Button>
            }
            {availableAssignment && <Button onClick={() => void submit(availableAssignment)} disabled={!canAssign}>
              {availableAssignment === "mine" ? "Ta ansvar" : "Släpp mitt ansvar"}</Button>}
            {((owner === "current_staff" && open) || (intent && "recipientUid" in intent.body)) && <>
              <CaseTransferRecipient key={scope} user={user!} value={chosenRecipient} blocked={frozen} onChange={setRecipient} onDenied={hideDenied} />
              <Button variant="outlined" onClick={() => void submit("transfer")} disabled={!canTransfer}>Bekräfta överlämning</Button>
            </>}
            <p>Ansvarsändring behåller status och bedömning. Granskning tilldelar dig ärendet. Ansvarsknapparna kan inte ta över en annan handläggares ärende.</p>
          </>}
          {kind === "withdrawal" && detail.state === "reviewed" &&
            <Alert severity="info">Bedömningen är sparad. En eventuell återbetalning kräver en separat orderåtgärd.</Alert>}
        </article>}
      </>}
  </div>;
}
